import { motion } from 'framer-motion';
import { FileText, FileUp, Globe, X } from 'lucide-react';
import {
  ChangeEvent,
  ComponentType,
  forwardRef,
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UrlInput } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/UrlInput';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { UploadFileHelper } from '@/Domain/helper/UploadFile.helper';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { checkDomainResolves } from '@/Domain/helper/dnsResolveCheck';
import {
  classifyKnownBadUrl,
  KnownBadUrlMatch,
  parseHost,
} from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { DEFAULT_ACCEPTED_FILE_TYPES } from '@/Domain/hooks/App/useUploadFile.hook';
import { cn } from '@/Domain/utils/cn';

/**
 * Second step of the manual onboarding checklist — Knowledge Base.
 *
 * Presents two side-by-side options for seeding the AI Agent's knowledge:
 *   - Left:  scrape a single web page. Clicking expands the card to full width
 *            and reveals a URL input (with a hard-coded `https://` prefix).
 *   - Right: upload a single document. Clicking opens the OS file picker.
 *            Once a file is selected, the preview tile expands to full width
 *            (mirroring the scrape expansion). The actual upload is deferred
 *            until the user clicks Continue on this step (the parent runs the
 *            `useUploadArticles` mutation at that point).
 *
 * File + URL state are lifted to the parent (`OnboardingProgress`) so the
 * Continue button on the checklist can kick off the appropriate action and
 * mark the step complete as a single action.
 *
 * The scrape/upload modes are mutually exclusive at the UI level — entering
 * scrape mode (non-null `url`) hides the upload tile and expands the scrape
 * card to full width; selecting a file (non-null `file`) hides the scrape tile
 * and expands the preview to full width. Both width + height morphs are
 * animated via `framer-motion`'s `layout` prop so the transition feels
 * consistent with the accordion-style step expansion in `OnboardingChecklist`.
 */

// Matches the default in `useUploadFile.hook.ts` (maxFileSize = 32). Kept in
// sync manually — the hook's value is a default parameter, not an exported const.
export const MAX_FILE_SIZE_MB = 32;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Shared easing for the scrape-mode transition. Matches the feel of the
// checklist's expand/collapse (see `OnboardingChecklist` — 0.2s with default
// easing). A slightly slower, eased curve looks smoother for layout morphs.
const MORPH_TRANSITION = { duration: 0.25, ease: [0.22, 1, 0.36, 1] } as const;

interface KnowledgeBaseStepProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  /**
   * Scrape URL. `null` = scrape mode not active (two-option grid shown).
   * Any string (including `''`) = scrape mode active (full-width URL panel).
   * Value matches what the KB modal's `UrlInput` emits — includes the
   * `https://` protocol for non-empty inputs (see `AppHelper.addProtocol`).
   */
  url: string | null;
  onUrlChange: (url: string | null) => void;
}

interface OptionTileProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  testId: string;
  onClick?: () => void;
  isLoading?: boolean;
}

const OptionTile = ({
  icon: Icon,
  title,
  subtitle,
  testId,
  onClick,
  isLoading,
}: OptionTileProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isLoading}
    data-testid={testId}
    className={cn(
      'group w-full h-full flex flex-col items-start gap-3 p-4 bg-card border border-border rounded-xl text-left cursor-pointer',
      // Hover fill matches the subtle tint used on completed items in
      // `OnboardingChecklist` — keeps the two card surfaces in the same page
      // visually related when the user mouses over a tile. Border stays put
      // so the tile outline doesn't flicker on hover.
      'transition-all duration-150 hover:bg-muted/80 hover:shadow-sm',
      isLoading && 'cursor-wait hover:bg-card hover:shadow-none',
    )}
  >
    <div className="size-9 rounded-lg bg-muted flex items-center justify-center">
      {isLoading ? (
        <Spinner className="size-[18px] text-foreground" />
      ) : (
        <Icon className="size-[18px] text-foreground" />
      )}
    </div>
    <div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </button>
);

interface FilePreviewTileProps {
  file: File;
  onClear: (e: MouseEvent) => void;
  clearLabel: string;
}

const FilePreviewTile = ({ file, onClear, clearLabel }: FilePreviewTileProps) => (
  <div
    className="relative w-full h-full flex flex-col items-start gap-3 p-4 bg-card border border-border rounded-xl text-left"
    data-testid="gs-kb-upload-preview"
  >
    <button
      type="button"
      onClick={onClear}
      aria-label={clearLabel}
      title={clearLabel}
      className="absolute top-2 right-2 size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
      data-testid="gs-kb-upload-preview-clear"
    >
      <X className="size-4" />
    </button>
    <div className="size-9 rounded-lg bg-muted flex items-center justify-center">
      <FileText className="size-[18px] text-foreground" />
    </div>
    <div className="min-w-0 max-w-full pr-6">
      <p
        className="text-sm font-medium text-foreground truncate"
        title={file.name}
        data-testid="gs-kb-upload-preview-name"
      >
        {file.name}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {UploadFileHelper.getFileSizeText(file.size)}
      </p>
    </div>
  </div>
);

interface ScrapeCardProps {
  url: string | null;
  onUrlChange: (url: string | null) => void;
  onOpen: () => void;
  title: string;
  subtitle: string;
  closeLabel: string;
  // Owned by the parent so `KnowledgeBaseStep.validate()` can flip it on
  // Continue and clear it as the user types. Keeping state out of this card
  // avoids a second source of truth that can drift from the Continue gate.
  urlError: boolean;
  // Host the DoH pre-check flagged as NXDOMAIN; null when no warning shows.
  unreachableHost: string | null;
  knownBadUrl: KnownBadUrlMatch | null;
}

/**
 * Morphing card: in collapsed state it looks like an `OptionTile`; in
 * expanded state it grows a URL input row below the icon/title. The single
 * animation for "expand" is driven by the outer `motion.div layout` in
 * `KnowledgeBaseStep` — this component just renders its current content and
 * lets the parent tween the bounding box. Nothing inside animates height, so
 * the outer `layout` measures the correct final size in one shot.
 *
 * The URL input is the shared `UrlInput` from the Knowledge Base upload
 * modal — same look, same protocol handling (`addProtocol`/`stripProtocol`),
 * same placeholder. Keeps this page and the KB modal in lockstep with one
 * component to maintain.
 */
const ScrapeCard = ({
  url,
  onUrlChange,
  onOpen,
  title,
  subtitle,
  closeLabel,
  urlError,
  unreachableHost,
  knownBadUrl,
}: ScrapeCardProps) => {
  const { t } = useTranslation('getting_started');
  const isExpanded = url !== null;

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    onUrlChange(null);
  };

  return (
    <div
      onClick={!isExpanded ? onOpen : undefined}
      role={!isExpanded ? 'button' : undefined}
      tabIndex={!isExpanded ? 0 : undefined}
      onKeyDown={
        !isExpanded
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      className={cn(
        'relative h-full w-full flex flex-col items-start gap-3 p-4 bg-card border rounded-xl text-left',
        'transition-colors duration-150',
        isExpanded
          ? 'border-border'
          : // Hover fill matches the subtle tint used on completed items in
            // `OnboardingChecklist` for visual consistency. Border stays at
            // `border-border` on hover so the outline doesn't flicker.
            'border-border hover:bg-muted/80 hover:shadow-sm cursor-pointer',
      )}
      data-testid={isExpanded ? 'gs-kb-scrape-panel' : 'gs-kb-scrape-option'}
    >
      {isExpanded && (
        <button
          type="button"
          onClick={handleClose}
          aria-label={closeLabel}
          title={closeLabel}
          className="absolute top-2 right-2 size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          data-testid="gs-kb-scrape-panel-close"
        >
          <X className="size-4" />
        </button>
      )}

      <div className="size-9 rounded-lg bg-muted flex items-center justify-center">
        <Globe className="size-[18px] text-foreground" />
      </div>
      {/* Title + subtitle are wrapped in a single div with the same styling
          as `OptionTile` so the collapsed scrape card matches the upload tile
          visually (icon, bold title, grey helper line). The subtitle is hidden
          in expanded mode since the URL input replaces the call-to-action. */}
      <div>
        <p className={cn('text-sm font-medium text-foreground', isExpanded && 'pr-6')}>{title}</p>
        {!isExpanded && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>

      {isExpanded && (
        // Rendered at final height immediately (no `height: 0 → auto`) so the
        // outer `layout` animation measures the correct target box and
        // morphs card width + height in a single motion. Only opacity is
        // animated, synced to the same duration/easing as the morph so the
        // input fades in *during* the growth, not after it.
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={MORPH_TRANSITION}
          className="w-full"
          data-testid="gs-kb-scrape-url-input"
        >
          <UrlInput
            type={WebsiteModalTypeEnum.SINGLE}
            value={url ?? ''}
            onChange={onUrlChange}
            error={urlError}
          />
          {unreachableHost && (
            <Alert variant="warning" className="mt-3" data-testid="gs-kb-scrape-unreachable">
              <AlertTitle>
                {t('domain_unreachable_notice.title', { host: unreachableHost })}
              </AlertTitle>
              <AlertDescription>
                {t('domain_unreachable_notice.description', { host: unreachableHost })}{' '}
                {t('knowledge_base_step.scrape.unreachable_hint')}
              </AlertDescription>
            </Alert>
          )}
          {knownBadUrl && (
            <Alert variant="warning" className="mt-3" data-testid="gs-kb-scrape-known-bad-url">
              <AlertTitle>{t(`known_bad_url_notice.${knownBadUrl.category}.title`)}</AlertTitle>
              <AlertDescription>
                {t(`known_bad_url_notice.${knownBadUrl.category}.description`, {
                  host: knownBadUrl.urlHost,
                })}
              </AlertDescription>
            </Alert>
          )}
        </motion.div>
      )}
    </div>
  );
};

export interface KnowledgeBaseStepHandle {
  /**
   * Resolves true when the step is safe to complete. Blocks on a non-empty
   * but malformed scrape URL (red border side effect) and on the first
   * Continue for a URL whose domain definitively fails the DoH existence
   * check (inline warning; a second Continue proceeds anyway). File-mode and
   * empty scrape input both pass (the parent skips the import call then).
   */
  validate: () => Promise<boolean>;
}

export const KnowledgeBaseStep = forwardRef<KnowledgeBaseStepHandle, KnowledgeBaseStepProps>(
  ({ file, onFileChange, url, onUrlChange }, ref) => {
    const { t } = useTranslation(['getting_started', 'knowledge_base']);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isScrapeMode = url !== null;
    // Driven exclusively by the imperative `validate()` call on Continue and
    // cleared the moment the user types anything new. We intentionally do not
    // hook `UrlInput`'s blur-based `onError` here — the Identity step uses the
    // same "only validate on Continue" pattern, and we keep the two steps
    // consistent.
    const [urlError, setUrlError] = useState(false);
    const [knownBadUrl, setKnownBadUrl] = useState<KnownBadUrlMatch | null>(null);
    // Host flagged NXDOMAIN by the DoH pre-check; showing the warning turns the
    // next Continue into "proceed anyway" (mirrors the automatic flow's
    // two-step confirm, adapted to the checklist's Continue-gated pattern).
    const [unreachableHost, setUnreachableHost] = useState<string | null>(null);
    // Spinner bridges the gap between click and the OS file dialog appearing.
    const [isOpeningFilePicker, setIsOpeningFilePicker] = useState(false);
    const { capture } = usePostHogAnalytics();
    // Latest url prop, so a validate() still awaiting the DoH lookup can tell its result is stale.
    const urlRef = useRef(url);
    urlRef.current = url;

    useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          // Not in scrape mode, or user opened scrape but typed nothing — let
          // the parent's Continue handler decide (it no-ops the import call).
          if (url === null || url === '') {
            setUrlError(false);
            return true;
          }
          const isValid = UploadUrlModalHelper.validateUrl(url);
          setUrlError(!isValid);
          if (!isValid) return false;
          // Otherwise the step completes on an import that is going to be rejected.
          const knownBad = classifyKnownBadUrl(url);
          if (knownBad) {
            // The notice stays up until an edit clears it, so a second Continue is not a new impression.
            const alreadyShown =
              knownBadUrl?.category === knownBad.category &&
              knownBadUrl?.urlHost === knownBad.urlHost;
            if (!alreadyShown) {
              capture(PostHogEventEnum.GettingStartedKnownBadUrlWarningShown, {
                category: knownBad.category,
                url_host: knownBad.urlHost,
                source: 'manual_kb_step',
              });
            }
            setKnownBadUrl(knownBad);
            return false;
          }
          const host = parseHost(url);
          if (!host) return true;
          if (unreachableHost === host) {
            // Second Continue on the warned host = deliberate override (e.g. a
            // freshly-registered domain still propagating).
            capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningResolved, {
              outcome: 'proceeded',
              url_host: host,
              source: 'manual_kb_step',
            });
            setUnreachableHost(null);
            return true;
          }
          // Same conservative DoH check as the automatic flow's URL form: only
          // a definitive NXDOMAIN blocks; resolver outages fall through.
          const resolution = await checkDomainResolves(url);
          // Input may have changed during the await; don't pin a stale verdict's warning/analytics to a URL the user already edited away from.
          if (urlRef.current !== url) return true;
          if (resolution === 'nxdomain') {
            capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningShown, {
              url_host: host,
              source: 'manual_kb_step',
            });
            setUnreachableHost(host);
            return false;
          }
          return true;
        },
      }),
      [url, unreachableHost, knownBadUrl, capture],
    );

    const handleUrlChange = (newUrl: string | null) => {
      if (urlError) setUrlError(false);
      if (knownBadUrl) setKnownBadUrl(null);
      if (unreachableHost) {
        capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningResolved, {
          outcome: 'corrected',
          url_host: unreachableHost,
          source: 'manual_kb_step',
        });
        setUnreachableHost(null);
      }
      onUrlChange(newUrl);
    };

    const handleUploadClick = () => {
      setIsOpeningFilePicker(true);
      // Defer .click() so the spinner paints before the OS dialog blocks the thread.
      requestAnimationFrame(() => {
        fileInputRef.current?.click();
      });
    };

    // Cancelling the file dialog doesn't fire `change`, so focus is our reset signal.
    useEffect(() => {
      if (!isOpeningFilePicker) return;
      const onFocus = () => setIsOpeningFilePicker(false);
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
    }, [isOpeningFilePicker]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsOpeningFilePicker(false);
      const selected = e.target.files?.[0];
      // Reset so picking the same file again still fires onChange.
      e.target.value = '';
      if (!selected) return;
      if (selected.size > MAX_FILE_SIZE_BYTES) {
        toast.error(
          t('knowledge_base:modals.upload_file.too_large', { maxFileSize: MAX_FILE_SIZE_MB }),
        );
        return;
      }
      // Android's picker ignores an extension-based `accept`, so this is the real gate. It is
      // deliberately the advertised subset, not everything the parser reads (see helpers.py).
      const extension = selected.name.includes('.')
        ? `.${selected.name.split('.').pop()?.toLowerCase()}`
        : '';
      if (!DEFAULT_ACCEPTED_FILE_TYPES.includes(extension)) {
        toast.error(
          t('knowledge_base:modals.upload_file.invalid_type', {
            acceptedFileTypes: DEFAULT_ACCEPTED_FILE_TYPES.join(', '),
          }),
        );
        return;
      }
      onFileChange(selected);
    };

    const handleClear = (e: MouseEvent) => {
      e.stopPropagation();
      onFileChange(null);
    };

    const handleScrapeOpen = () => {
      // Entering scrape mode: switch URL from null to empty string. The parent
      // uses `null` as the signal that scrape mode is inactive.
      onUrlChange('');
    };

    return (
      // `flex` instead of `grid` so either card can smoothly grow into the
      // space vacated by its sibling. The inactive tile unmounts immediately
      // (no exit animation) — letting it fade out would run its shrink animation
      // concurrently with the active card's growth and the two motions visibly
      // fight each other. With instant unmount, `motion.div layout` on the
      // remaining wrapper animates cleanly from 50% → 100% with nothing in the
      // way. Symmetric for both directions: scrape grows when upload unmounts
      // (entering scrape mode), upload grows when scrape unmounts (file picked).
      // `min-h-[156px]` reserves the expanded-state height even when both tiles
      // are collapsed. Math (border-box includes borders in the declared height):
      //   card border (2) + p-4 × 2 (32) + icon (36) + gap-3 × 2 (24)
      //   + title text-sm (20) + Input outer div h-[39px] + its border (41) = 155
      // Rounded up to 156 for sub-pixel safety. With `items-stretch` (default on
      // flex), all children stretch to this height, so clicking a tile only
      // animates width, never height. Collapsed tiles show a little extra
      // whitespace at the bottom — reads as breathing room, not a layout shift.
      <div className="flex gap-3 min-h-[156px]" data-testid="gs-knowledge-base-step">
        {!file && (
          <motion.div layout transition={MORPH_TRANSITION} className="flex-1 min-w-0">
            <ScrapeCard
              url={url}
              onUrlChange={handleUrlChange}
              onOpen={handleScrapeOpen}
              title={t('knowledge_base_step.scrape.title')}
              subtitle={t('knowledge_base_step.scrape.subtitle')}
              closeLabel={t('knowledge_base_step.scrape.close')}
              urlError={urlError}
              unreachableHost={unreachableHost}
              knownBadUrl={knownBadUrl}
            />
          </motion.div>
        )}

        {!isScrapeMode && (
          <motion.div layout transition={MORPH_TRANSITION} className="flex-1 min-w-0">
            {file ? (
              <FilePreviewTile
                file={file}
                onClear={handleClear}
                clearLabel={t('knowledge_base_step.upload.clear')}
              />
            ) : (
              <OptionTile
                icon={FileUp}
                title={t('knowledge_base_step.upload.title')}
                subtitle={t('knowledge_base_step.upload.max_size', {
                  maxFileSize: MAX_FILE_SIZE_MB,
                })}
                testId="gs-kb-upload-option"
                onClick={handleUploadClick}
                isLoading={isOpeningFilePicker}
              />
            )}
          </motion.div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={DEFAULT_ACCEPTED_FILE_TYPES.join(',')}
          onChange={handleFileChange}
          data-testid="gs-kb-upload-input"
        />
      </div>
    );
  },
);

KnowledgeBaseStep.displayName = 'KnowledgeBaseStep';
